import React, { useEffect, useState } from 'react'
import { Api, Endpoint } from '../../api/index'
import { buildOrderLineItems } from '../../helpers/ordering'
import { attachSquareCard, tokenizeSquareCard } from '../../utils/squarePayments'
import general from '../../helpers/general'
import styles from './CheckoutForm.module.scss'

function isValidEmail(value) {
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

export default function CheckoutForm({
	restaurantId,
	cartItems,
	total,
	checkoutConfig,
	onBack,
	onSuccess,
}) {
	const [guestName, setGuestName] = useState('')
	const [guestEmail, setGuestEmail] = useState('')
	const [errors, setErrors] = useState({})
	const [submitError, setSubmitError] = useState('')
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [cardReady, setCardReady] = useState(false)
	const [card, setCard] = useState(null)

	useEffect(() => {
		if (!checkoutConfig?.applicationId || !checkoutConfig?.locationId) return undefined

		let active = true
		setCardReady(false)

		attachSquareCard(
			checkoutConfig.applicationId,
			checkoutConfig.locationId,
			'#square-card-container',
		)
			.then(({ card: squareCard }) => {
				if (!active) return
				setCard(squareCard)
				setCardReady(true)
			})
			.catch((err) => {
				if (!active) return
				setSubmitError(err.message || 'Unable to load card payment form.')
			})

		return () => {
			active = false
		}
	}, [checkoutConfig?.applicationId, checkoutConfig?.locationId])

	function validate() {
		const nextErrors = {}
		if (!guestName.trim()) nextErrors.name = 'Name is required.'
		if (!guestEmail.trim()) nextErrors.email = 'Email is required.'
		else if (!isValidEmail(guestEmail.trim())) nextErrors.email = 'Enter a valid email address.'
		setErrors(nextErrors)
		return Object.keys(nextErrors).length === 0
	}

	async function handleSubmit(event) {
		event.preventDefault()
		setSubmitError('')
		if (!validate()) return
		if (!card) {
			setSubmitError('Card payment form is not ready yet.')
			return
		}

		setIsSubmitting(true)
		try {
			const paymentSourceToken = await tokenizeSquareCard(card)
			const response = await Api.call(Endpoint.orders.create, {
				data: {
					restaurantId,
					guestName: guestName.trim(),
					guestEmail: guestEmail.trim(),
					paymentSourceToken,
					lineItems: buildOrderLineItems(cartItems),
				},
			})

			if (response?.confirmation === 'Success' && response?.message?.orderId) {
				onSuccess(response.message.orderId)
				return
			}

			setSubmitError(response?.message || 'Payment failed. Please try again.')
		} catch (err) {
			setSubmitError(err.message || 'Payment failed. Please try again.')
		} finally {
			setIsSubmitting(false)
		}
	}

	return (
		<form className={styles.form} onSubmit={handleSubmit} noValidate>
			<header className={styles.header}>
				<button
					type="button"
					className={styles.backBtn}
					data-testid="checkout-back"
					aria-label="Back to cart"
					onClick={onBack}
					disabled={isSubmitting}
				>
					← Back
				</button>
				<h1 className={styles.title}>Checkout</h1>
			</header>

			<label className={styles.field}>
				<span>Name</span>
				<input
					type="text"
					value={guestName}
					onChange={(event) => setGuestName(event.target.value)}
					aria-label="Name"
					autoComplete="name"
				/>
				{errors.name && <span className={styles.error}>{errors.name}</span>}
			</label>

			<label className={styles.field}>
				<span>Email</span>
				<input
					type="email"
					value={guestEmail}
					onChange={(event) => setGuestEmail(event.target.value)}
					aria-label="Email"
					autoComplete="email"
				/>
				{errors.email && <span className={styles.error}>{errors.email}</span>}
			</label>

			<div className={styles.cardSection}>
				<span>Card</span>
				<div id="square-card-container" data-testid="square-card-container" />
				{!cardReady && !submitError && <span className={styles.hint}>Loading secure card fields…</span>}
			</div>

			<div className={styles.summary}>
				<span>Total</span>
				<span>{general.formatPrice(total)}</span>
			</div>

			{submitError && (
				<p className={styles.submitError} role="alert">
					{submitError}
				</p>
			)}

			<button type="submit" className={styles.submitBtn} disabled={isSubmitting}>
				{isSubmitting ? 'Processing…' : `Pay ${general.formatPrice(total)}`}
			</button>
		</form>
	)
}
